// src/pages/admin/AdminEmployees.jsx
// ============================================================
// Admin creates / manages employees.
// Handles removal requests from Team Leads (requiring Admin approval).
// Includes the complete Employee Onboarding Wizard matching BRD 1.0.
// ============================================================

import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { EMS_DEPARTMENTS } from "../../utils/hrConstants";
import { useAllUsers, useDepartments, useCollection } from "../../hooks/useFirestore";
import {
  useShifts,
  usePendingRemovalRequests,
  approveRemovalRequest,
  rejectRemovalRequest,
} from "../../hooks/useFirestoreExtensions";
import {
  useEmployeePersonalDetails,
  useEmployeeBankDetails,
  logToTimeline,
  logToAudit,
} from "../../hooks/useEmployeeMaster";
import {
  validateAadhaar,
  validatePAN,
  validateIFSC,
  validateMobile,
  validateEmergencyContact,
} from "../../utils/validation";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import EmployeeOverviewDrawer from "../../components/common/EmployeeOverviewDrawer";
import {
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import * as XLSX from "xlsx";
const ROLE_LABELS = {
  admin: "Admin",
  hr: "HR",
  lead: "Team Lead",
  employee: "Employee",
  finance: "Finance",
};

export default function AdminEmployees() {
  const { signup, userProfile } = useAuth();
  const { data: shifts } = useShifts();
  const { data: users, loading } = useAllUsers();
  const { departments } = useDepartments();
  
  const activeDepts = departments && departments.length > 0
    ? departments.filter((d) => d.active).map((d) => d.name)
    : EMS_DEPARTMENTS;

  const { data: pendingRemovalRequests, loading: removalReqsLoading } =
    usePendingRemovalRequests();
  const leads = users.filter((u) => u.role === "lead");

  // Master Data Hook Lookups (removed)

  const [showForm, setShowForm] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [approvingRemoval, setApprovingRemoval] = useState({});
  const [rejectingRemoval, setRejectingRemoval] = useState({});
  const [removalMsg, setRemovalMsg] = useState("");

  const [currentStep, setCurrentStep] = useState(1);
  const [editTab, setEditTab] = useState("employment");

  // Onboarding Wizard Form State
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    displayName: "",
    email: "",
    password: "",
    role: "employee",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "Male",
    dateOfBirth: "",
    maritalStatus: "Single",
    bloodGroup: "O+",
    nationality: "Indian",
    personalEmail: "",
    phone: "",
    alternateMobile: "",
    emergencyContact: "",

    // Step 2: Employment Details
    joiningDate: "",
    confirmationDate: "",
    probationPeriod: "6",
    employmentType: "full-time",
    department: "Engineering",
    subDepartmentId: "",
    shiftId: "",
    reportingManagerId: "",
    hrManagerId: "",
    teamId: "",
    internshipDuration: "3",

    // Step 3: Personal & Address
    fatherName: "",
    motherName: "",
    spouseName: "",
    children: [],
    aadhaar: "",
    pan: "",
    passport: "",
    drivingLicense: "",
    voterId: "",
    passportExpiry: "",
    visa: "",
    permLine1: "",
    permLine2: "",
    permCity: "",
    permState: "",
    permCountry: "India",
    permPincode: "",
    currLine1: "",
    currLine2: "",
    currCity: "",
    currState: "",
    currCountry: "India",
    currPincode: "",
    sameAsPermanent: false,

    // Step 4: Bank & Payroll
    bankName: "",
    bankBranch: "",
    accountNumber: "",
    ifscCode: "",
    accountHolder: "",
    upiId: "",
    isSalaryAccount: true,
    ctc: "0",
    basicSalary: "0",
    hra: "0",
    allowances: [],
    taxRegime: "New Regime",
    pfApplicable: true,
    pfNumber: "",
    esiApplicable: false,
    esiNumber: "",
    professionalTax: true,
    lwf: true,
    tdsCategoryId: "",
    employeeId: "",
  });

  // Edit Employee Form State
  const [editData, setEditData] = useState({
    displayName: "",
    email: "",
    role: "employee",
    department: "Engineering",
    employeeId: "",
    teamId: "",
    shiftId: "",
    employmentType: "full-time",
    joiningDate: "",
    internshipDuration: "3",
    isActive: true,

    firstName: "",
    middleName: "",
    lastName: "",
    gender: "Male",
    dateOfBirth: "",
    maritalStatus: "Single",
    bloodGroup: "O+",
    nationality: "Indian",
    personalEmail: "",
    phone: "",
    alternateMobile: "",
    emergencyContact: "",

    confirmationDate: "",
    probationPeriod: "6",
    subDepartmentId: "",
    hrManagerId: "",

    fatherName: "",
    motherName: "",
    spouseName: "",
    aadhaar: "",
    pan: "",
    passport: "",
    drivingLicense: "",
    voterId: "",
    passportExpiry: "",
    visa: "",
    permLine1: "",
    permLine2: "",
    permCity: "",
    permState: "",
    permCountry: "India",
    permPincode: "",
    currLine1: "",
    currLine2: "",
    currCity: "",
    currState: "",
    currCountry: "India",
    currPincode: "",
    sameAsPermanent: false,

    bankName: "",
    bankBranch: "",
    accountNumber: "",
    ifscCode: "",
    accountHolder: "",
    upiId: "",
    isSalaryAccount: true,
    ctc: "0",
    basicSalary: "0",
    hra: "0",
    taxRegime: "New Regime",
    pfApplicable: true,
    pfNumber: "",
    esiApplicable: false,
    esiNumber: "",
    professionalTax: true,
    lwf: true,
    tdsCategoryId: "",
  });

  // Load editing user's subcollections dynamically
  const { data: editPersonal } = useEmployeePersonalDetails(editingUser?.uid);
  const { data: editBank } = useEmployeeBankDetails(editingUser?.uid);

  useEffect(() => {
    if (editingUser && editPersonal) {
      setEditData((p) => ({
        ...p,
        fatherName: editPersonal.fatherName || "",
        motherName: editPersonal.motherName || "",
        spouseName: editPersonal.spouseName || "",
        aadhaar: editPersonal.aadhaar || "",
        pan: editPersonal.pan || "",
        passport: editPersonal.passport || "",
        drivingLicense: editPersonal.drivingLicense || "",
        voterId: editPersonal.voterId || "",
        passportExpiry: editPersonal.passportExpiry || "",
        visa: editPersonal.visa || "",
        permLine1: editPersonal.permanentAddress?.line1 || "",
        permLine2: editPersonal.permanentAddress?.line2 || "",
        permCity: editPersonal.permanentAddress?.city || "",
        permState: editPersonal.permanentAddress?.state || "",
        permCountry: editPersonal.permanentAddress?.country || "India",
        permPincode: editPersonal.permanentAddress?.pincode || "",
        currLine1: editPersonal.currentAddress?.line1 || "",
        currLine2: editPersonal.currentAddress?.line2 || "",
        currCity: editPersonal.currentAddress?.city || "",
        currState: editPersonal.currentAddress?.state || "",
        currCountry: editPersonal.currentAddress?.country || "India",
        currPincode: editPersonal.currentAddress?.pincode || "",
        sameAsPermanent: editPersonal.currentAddress?.sameAsPermanent || false,
      }));
    }
  }, [editingUser, editPersonal]);

  useEffect(() => {
    if (editingUser && editBank) {
      setEditData((p) => ({
        ...p,
        bankName: editBank.bankName || "",
        bankBranch: editBank.branch || "",
        accountNumber: editBank.accountNumber || "",
        ifscCode: editBank.ifscCode || "",
        accountHolder: editBank.accountHolder || "",
        upiId: editBank.upiId || "",
        isSalaryAccount: editBank.isSalaryAccount !== undefined ? editBank.isSalaryAccount : true,
        ctc: String(editBank.ctc || "0"),
        basicSalary: String(editBank.basicSalary || "0"),
        hra: String(editBank.hra || "0"),
        taxRegime: editBank.taxRegime || "New Regime",
        pfApplicable: editBank.pfApplicable !== undefined ? editBank.pfApplicable : true,
        pfNumber: editBank.pfNumber || "",
        esiApplicable: editBank.esiApplicable !== undefined ? editBank.esiApplicable : false,
        esiNumber: editBank.esiNumber || "",
        professionalTax: editBank.professionalTax !== undefined ? editBank.professionalTax : true,
        lwf: editBank.lwf !== undefined ? editBank.lwf : true,
        tdsCategoryId: editBank.tdsCategoryId || "",
      }));
    }
  }, [editingUser, editBank]);

  // Admin password confirmation state
  const [adminPassword, setAdminPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createOk, setCreateOk] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [filterIntern, setFilterIntern] = useState("all");

  const [sortBy, setSortBy] = useState("newest");
  const [autoGenerateId, setAutoGenerateId] = useState(true);
  const { data: archivedUsers } = useCollection("archived_users");

  const filtered = users.filter((u) => {
    const isInternEmp = (type) => type === "paid-intern" || type === "unpaid-intern" || type === "intern";
    const daysLeft = u.internshipEndDate ? Math.ceil((new Date(u.internshipEndDate) - new Date()) / 86400000) : null;
    const isCompleted = isInternEmp(u.employmentType) && (u.internshipStatus === "completed" || u.internshipStatus === "converted" || (daysLeft !== null && daysLeft <= 0));
    const isCompletingSoon = isInternEmp(u.employmentType) && daysLeft !== null && daysLeft > 0 && daysLeft <= 15;

    const sq = searchQ.toLowerCase().trim();
    const matchQ =
      u.displayName.toLowerCase().includes(sq) ||
      u.email.toLowerCase().includes(sq) ||
      (u.employeeId || "").toLowerCase().includes(sq) ||
      (sq === "completed" && isCompleted) ||
      ((sq === "completing" || sq === "completing soon") && isCompletingSoon);

    const matchRole = filterRole === "all" || u.role === filterRole;
    const matchDept = filterDept === "all" || u.department === filterDept;
    let matchIntern = true;
    if (filterIntern === "intern") matchIntern = isInternEmp(u.employmentType);
    else if (filterIntern === "full-time") matchIntern = !isInternEmp(u.employmentType);
    else if (filterIntern === "paid-intern") matchIntern = u.employmentType === "paid-intern" || u.employmentType === "intern";
    else if (filterIntern === "unpaid-intern") matchIntern = u.employmentType === "unpaid-intern";
    else if (filterIntern === "completing") matchIntern = isCompletingSoon;
    else if (filterIntern === "completed") matchIntern = isCompleted;

    return matchQ && matchRole && matchDept && matchIntern;
  });

  const sortedAndFiltered = [...filtered].sort((a, b) => {
    if (sortBy === "newest") {
      const t1 = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt ? new Date(a.createdAt).getTime() : 0));
      const t2 = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt ? new Date(b.createdAt).getTime() : 0));
      return t2 - t1;
    } else if (sortBy === "oldest") {
      const t1 = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt ? new Date(a.createdAt).getTime() : 0));
      const t2 = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt ? new Date(b.createdAt).getTime() : 0));
      return t1 - t2;
    } else if (sortBy === "a-z") {
      return (a.displayName || "").localeCompare(b.displayName || "");
    } else if (sortBy === "z-a") {
      return (b.displayName || "").localeCompare(a.displayName || "");
    }
    return 0;
  });

  // Export the currently filtered employee list (respecting Department/Role/search/etc.
  // filters) to an Excel file with columns: Name | ID | Email | Department | Role | Status
  function handleExportExcel() {
    const rows = sortedAndFiltered.map((u) => ({
      Name: u.displayName || "",
      ID: u.employeeId || "",
      Email: u.email || "",
      Department: u.department || "",
      Role: ROLE_LABELS[u.role] || u.role || "",
      Status: u.isActive ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: ["Name", "ID", "Email", "Department", "Role", "Status"],
    });
    worksheet["!cols"] = [
      { wch: 24 }, // Name
      { wch: 14 }, // ID
      { wch: 28 }, // Email
      { wch: 18 }, // Department
      { wch: 14 }, // Role
      { wch: 10 }, // Status
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    const deptLabel = filterDept === "all" ? "AllDepartments" : filterDept.replace(/\s+/g, "");
    const roleLabel = filterRole === "all" ? "AllRoles" : (ROLE_LABELS[filterRole] || filterRole).replace(/\s+/g, "");
    const dateStamp = new Date().toISOString().split("T")[0];
    const fileName = `Employee_Report_${deptLabel}_${roleLabel}_${dateStamp}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  }

  function handleChange(e) {
  const { name, value } = e.target;
  setFormData((p) => {
    const next = { ...p, [name]: value };
    if (name === "role") {
      if (value === "admin" || value === "lead" || value === "hr" || value === "finance") {
        next.employmentType = "full-time";
      }
      if (value === "admin") {
        next.department = "Administration";
      } else if (value === "hr") {
        next.department = "HR";
      } else if (value === "finance") {
        next.department = "Finance";
      } else if (value === "lead" && ["Administration", "HR", "Finance"].includes(next.department)) {
        next.department = "Engineering";
      }
    }
    return next;
  });
}
  function handleEditChange(e) {
  const { name, value, type, checked } = e.target;
  setEditData((p) => {
    const next = { ...p, [name]: type === "checkbox" ? checked : value };
    if (name === "role") {
      if (value === "admin" || value === "lead" || value === "hr" || value === "finance") {
        next.employmentType = "full-time";
      }
      if (value === "admin") {
        next.department = "Administration";
      } else if (value === "hr") {
        next.department = "HR";
      } else if (value === "finance") {
        next.department = "Finance";
      } else if (value === "lead" && ["Administration", "HR", "Finance"].includes(next.department)) {
        next.department = "Engineering";
      }
    }
    return next;
  });
}

  // Validate current wizard step fields client-side
  function validateStep(step) {
    const errors = {};
    setCreateErr("");

    if (step === 1) {
      if (!formData.displayName.trim()) errors.displayName = "Full name is required";
      if (!formData.email.trim()) {
        errors.email = "Email is required";
      } else {
        const emailRegex = /^[A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!emailRegex.test(formData.email.trim())) errors.email = "Please enter a valid email address";
      }
      if (!formData.password.trim()) {
        errors.password = "Password is required";
      } else if (formData.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }
      if (formData.phone && !validateMobile(formData.phone)) {
        errors.phone = "Invalid primary mobile number (10-15 digits)";
      }
      if (formData.alternateMobile && !validateMobile(formData.alternateMobile)) {
        errors.alternateMobile = "Invalid alternate mobile number";
      }
      if (formData.dateOfBirth) {
        const jd = formData.joiningDate ? new Date(formData.joiningDate) : new Date();
        const dob = new Date(formData.dateOfBirth);
        let age = jd.getFullYear() - dob.getFullYear();
        const m = jd.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && jd.getDate() < dob.getDate())) {
          age--;
        }
        if (age < 18) errors.dateOfBirth = "Employee must be at least 18 years old at joining";
      }
    }

    if (step === 2) {
      if (!formData.joiningDate.trim()) errors.joiningDate = "Date of Joining is required";
      if (!formData.department.trim()) errors.department = "Missing department";
      
      const isInternForm = formData.employmentType === "paid-intern" || formData.employmentType === "unpaid-intern";
      if (isInternForm) {
        const dur = parseInt(formData.internshipDuration, 10);
        if (!formData.internshipDuration || isNaN(dur) || dur < 1 || dur > 24) {
          errors.internshipDuration = "Internship duration must be between 1 and 24 months";
        }
      }
    }

    if (step === 3) {
      if (formData.aadhaar && !validateAadhaar(formData.aadhaar)) {
        errors.aadhaar = "Invalid Aadhaar format or checksum verification failed";
      }
      if (formData.pan && !validatePAN(formData.pan)) {
        errors.pan = "Invalid PAN card format (AAAAA9999A)";
      }
      if (formData.emergencyContact && !validateMobile(formData.emergencyContact)) {
        errors.emergencyContact = "Invalid emergency contact number";
      } else if (formData.emergencyContact && !validateEmergencyContact(formData.emergencyContact, formData.phone, formData.alternateMobile)) {
        errors.emergencyContact = "Emergency contact must differ from employee's primary and alternate numbers";
      }
      if (!formData.permLine1.trim() || !formData.permCity.trim() || !formData.permState.trim() || !formData.permPincode.trim()) {
        errors.permanentAddress = "A complete Permanent Address is required";
      }
    }

    if (step === 4) {
      if (!formData.bankName.trim()) errors.bankName = "Bank Name is required";
      if (!formData.accountNumber.trim()) errors.accountNumber = "Bank Account number is required";
      if (!formData.ifscCode.trim()) {
        errors.ifscCode = "IFSC code is required";
      } else if (!validateIFSC(formData.ifscCode)) {
        errors.ifscCode = "Invalid IFSC Code format";
      }
      if (!formData.accountHolder.trim()) errors.accountHolder = "Account Holder name is required";
      if (parseFloat(formData.ctc) <= 0) errors.ctc = "Annual CTC must be greater than 0";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  async function handleCreate(e) {
    e.preventDefault();

    if (!validateStep(4)) return;
    if (!adminPassword.trim()) {
      setCreateErr("Admin password is required to confirm account creation");
      return;
    }

    setCreating(true);
    setCreateErr("");
    setCreateOk("");

    try {
      // 1. Sign up basic Auth & Firestore user profile
      const newUser = await signup({
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName,
        role: formData.role,
        department: formData.department,
        employeeId: autoGenerateId ? "" : formData.employeeId,
        teamId: formData.role === "employee" ? formData.teamId || null : null,
        employmentType: formData.employmentType,
        joiningDate: formData.joiningDate,
        internshipDuration: formData.internshipDuration,
        adminEmail: userProfile.email,
        adminPassword,
      });

      if (!newUser) throw new Error("Failed to register auth account.");
      const uid = newUser.uid;

      // 2. Update users collection document with extra master parameters
      await updateDoc(doc(db, "users", uid), {
        firstName: formData.firstName || "",
        middleName: formData.middleName || "",
        lastName: formData.lastName || "",
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth || null,
        maritalStatus: formData.maritalStatus,
        bloodGroup: formData.bloodGroup,
        nationality: formData.nationality,
        personalEmail: formData.personalEmail || "",
        phone: formData.phone || "",
        alternateMobile: formData.alternateMobile || "",
        emergencyContact: formData.emergencyContact || "",
        
        confirmationDate: formData.confirmationDate || null,
        probationPeriod: parseInt(formData.probationPeriod) || 0,
        subDepartmentId: formData.subDepartmentId || null,
        shiftId: formData.shiftId || null,
        hrManagerId: formData.hrManagerId || null,
        status: "Active",
        mfaEnabled: false,
      });

      // 3. Populate personal_details subcollection
      await setDoc(doc(db, "users", uid, "personal_details", "main"), {
        fatherName: formData.fatherName || "",
        motherName: formData.motherName || "",
        spouseName: formData.spouseName || "",
        children: formData.children || [],
        aadhaar: formData.aadhaar || "",
        pan: formData.pan || "",
        passport: formData.passport || "",
        drivingLicense: formData.drivingLicense || "",
        voterId: formData.voterId || "",
        passportExpiry: formData.passportExpiry || null,
        visa: formData.visa || "",
        permanentAddress: {
          line1: formData.permLine1 || "",
          line2: formData.permLine2 || "",
          city: formData.permCity || "",
          state: formData.permState || "",
          country: formData.permCountry || "India",
          pincode: formData.permPincode || ""
        },
        currentAddress: {
          line1: (formData.sameAsPermanent ? formData.permLine1 : formData.currLine1) || "",
          line2: (formData.sameAsPermanent ? formData.permLine2 : formData.currLine2) || "",
          city: (formData.sameAsPermanent ? formData.permCity : formData.currCity) || "",
          state: (formData.sameAsPermanent ? formData.permState : formData.currState) || "",
          country: (formData.sameAsPermanent ? formData.permCountry : formData.currCountry) || "India",
          pincode: (formData.sameAsPermanent ? formData.permPincode : formData.currPincode) || "",
          sameAsPermanent: formData.sameAsPermanent
        },
        updatedAt: serverTimestamp(),
      });

      // 4. Populate bank_details subcollection
      await setDoc(doc(db, "users", uid, "bank_details", "main"), {
        bankName: formData.bankName || "",
        branch: formData.bankBranch || "",
        accountNumber: formData.accountNumber || "",
        ifscCode: formData.ifscCode || "",
        accountHolder: formData.accountHolder || "",
        upiId: formData.upiId || "",
        isSalaryAccount: formData.isSalaryAccount,
        
        ctc: parseFloat(formData.ctc) || 0,
        basicSalary: parseFloat(formData.basicSalary) || 0,
        hra: parseFloat(formData.hra) || 0,
        allowances: formData.allowances || [],
        taxRegime: formData.taxRegime,
        pfApplicable: formData.pfApplicable,
        pfNumber: formData.pfNumber || "",
        esiApplicable: formData.esiApplicable,
        esiNumber: formData.esiNumber || "",
        professionalTax: formData.professionalTax,
        lwf: formData.lwf,
        tdsCategoryId: formData.tdsCategoryId || "",
        updatedAt: serverTimestamp(),
      });

      // 5. Log Timeline & Audit Entries
      await logToTimeline(uid, "Created", "Employee Onboarded", `Employee account created and master record initialized by Admin.`, userProfile.displayName);
      await logToAudit(uid, formData.displayName, userProfile.uid, userProfile.displayName, "WRITE", "users", "", "CREATED");

      setCreateOk("Employee account created successfully");
      
      // Reset State
      setFormData({
        displayName: "", email: "", password: "", role: "employee",
        firstName: "", middleName: "", lastName: "", gender: "Male", dateOfBirth: "",
        maritalStatus: "Single", bloodGroup: "O+", nationality: "Indian", personalEmail: "",
        phone: "", alternateMobile: "", emergencyContact: "",
        joiningDate: "", confirmationDate: "", probationPeriod: "6", employmentType: "full-time",
        department: "Engineering", subDepartmentId: "", shiftId: "",
        reportingManagerId: "", hrManagerId: "", teamId: "", internshipDuration: "3", fatherName: "", motherName: "", spouseName: "",
        children: [], aadhaar: "", pan: "", passport: "", drivingLicense: "", voterId: "",
        passportExpiry: "", visa: "", permLine1: "", permLine2: "", permCity: "", permState: "",
        permCountry: "India", permPincode: "", currLine1: "", currLine2: "", currCity: "",
        currState: "", currCountry: "India", currPincode: "", sameAsPermanent: false,
        bankName: "", bankBranch: "", accountNumber: "", ifscCode: "", accountHolder: "",
        upiId: "", isSalaryAccount: true, ctc: "0", basicSalary: "0", hra: "0", allowances: [],
        taxRegime: "New Regime", pfApplicable: true, pfNumber: "", esiApplicable: false,
        esiNumber: "", professionalTax: true, lwf: true, tdsCategoryId: "", employeeId: "",
      });
      setAdminPassword("");
      setCurrentStep(1);
      setShowForm(false);
    } catch (err) {
      setCreateErr(err.message || "Failed to create user.");
    } finally {
      setCreating(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    setCreateErr("");
    setCreateOk("");
    setUpdating(true);

    try {
      const lead = leads.find((l) => l.uid === editData.teamId);
      const normalizedType = editData.employmentType === "intern" ? "paid-intern" : (editData.employmentType || "full-time");
      const isInternEdit = normalizedType === "paid-intern" || normalizedType === "unpaid-intern";

      let internFields = {};
      if (isInternEdit && editData.joiningDate) {
        const dur = parseInt(editData.internshipDuration, 10) || 3;
        const jd = new Date(editData.joiningDate);
        const internEnd = new Date(jd);
        if (dur === 3) {
          internEnd.setDate(internEnd.getDate() + 90);
        } else {
          internEnd.setMonth(internEnd.getMonth() + dur);
        }
        internFields = {
          internshipStartDate: editData.joiningDate,
          internshipEndDate: internEnd.toISOString().split("T")[0],
          internshipDuration: dur,
        };
      }

      const updates = {
        displayName: editData.displayName,
        email: editData.email,
        role: editData.role,
        department: editData.department,
        employeeId: editData.employeeId,
        teamId: editData.role === "employee" ? editData.teamId || null : null,
        managerId: editData.role === "employee" ? editData.teamId || null : null,
        teamLeadId: editData.role === "employee" ? editData.teamId || null : null,
        teamLeadName: editData.role === "employee" ? lead?.displayName || "" : "",
        shiftId: editData.shiftId || null,
        employmentType: normalizedType,
        ...(editData.joiningDate ? { joiningDate: editData.joiningDate } : {}),
        ...internFields,
        isActive: editData.isActive,
        updatedAt: serverTimestamp(),

        firstName: editData.firstName || "",
        middleName: editData.middleName || "",
        lastName: editData.lastName || "",
        gender: editData.gender || "Male",
        dateOfBirth: editData.dateOfBirth || null,
        maritalStatus: editData.maritalStatus || "Single",
        bloodGroup: editData.bloodGroup || "O+",
        nationality: editData.nationality || "Indian",
        personalEmail: editData.personalEmail || "",
        phone: editData.phone || "",
        alternateMobile: editData.alternateMobile || "",
        emergencyContact: editData.emergencyContact || "",

        confirmationDate: editData.confirmationDate || null,
        probationPeriod: parseInt(editData.probationPeriod) || 0,
        subDepartmentId: editData.subDepartmentId || null,
        hrManagerId: editData.hrManagerId || null,
      };

      await updateDoc(doc(db, "users", editingUser.uid), updates);

      // Update personal subcollection
      await setDoc(doc(db, "users", editingUser.uid, "personal_details", "main"), {
        fatherName: editData.fatherName || "",
        motherName: editData.motherName || "",
        spouseName: editData.spouseName || "",
        aadhaar: editData.aadhaar || "",
        pan: editData.pan || "",
        passport: editData.passport || "",
        drivingLicense: editData.drivingLicense || "",
        voterId: editData.voterId || "",
        passportExpiry: editData.passportExpiry || null,
        visa: editData.visa || "",
        permanentAddress: {
          line1: editData.permLine1 || "",
          line2: editData.permLine2 || "",
          city: editData.permCity || "",
          state: editData.permState || "",
          country: editData.permCountry || "India",
          pincode: editData.permPincode || ""
        },
        currentAddress: {
          line1: (editData.sameAsPermanent ? editData.permLine1 : editData.currLine1) || "",
          line2: (editData.sameAsPermanent ? editData.permLine2 : editData.currLine2) || "",
          city: (editData.sameAsPermanent ? editData.permCity : editData.currCity) || "",
          state: (editData.sameAsPermanent ? editData.permState : editData.currState) || "",
          country: (editData.sameAsPermanent ? editData.permCountry : editData.currCountry) || "India",
          pincode: (editData.sameAsPermanent ? editData.permPincode : editData.currPincode) || "",
          sameAsPermanent: editData.sameAsPermanent
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Update bank/payroll subcollection
      await setDoc(doc(db, "users", editingUser.uid, "bank_details", "main"), {
        bankName: editData.bankName || "",
        branch: editData.bankBranch || "",
        accountNumber: editData.accountNumber || "",
        ifscCode: editData.ifscCode || "",
        accountHolder: editData.accountHolder || "",
        upiId: editData.upiId || "",
        isSalaryAccount: editData.isSalaryAccount,
        ctc: parseFloat(editData.ctc) || 0,
        basicSalary: parseFloat(editData.basicSalary) || 0,
        hra: parseFloat(editData.hra) || 0,
        taxRegime: editData.taxRegime,
        pfApplicable: editData.pfApplicable,
        pfNumber: editData.pfNumber || "",
        esiApplicable: editData.esiApplicable,
        esiNumber: editData.esiNumber || "",
        professionalTax: editData.professionalTax,
        lwf: editData.lwf,
        tdsCategoryId: editData.tdsCategoryId || "",
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await logToTimeline(editingUser.uid, "FieldUpdate", "Profile Updated by Admin", `Profile details updated by Admin.`, userProfile.displayName);
      await logToAudit(editingUser.uid, editData.displayName, userProfile.uid, userProfile.displayName, "WRITE", "users", "EDITED", "SUCCESS");

      setCreateOk(`✅ ${editData.displayName} updated successfully.`);
      setShowEdit(false);
      setEditingUser(null);
    } catch (err) {
      setCreateErr(err.message || "Failed to update user.");
    } finally {
      setUpdating(false);
    }
  }

  async function removeEmployee(user) {
    if (user.uid === userProfile?.uid) {
      setCreateErr("You cannot remove your own account.");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to permanently Deactivate ${user.displayName}?`
    );
    if (!confirmDelete) return;

    try {
      const archiveData = {
        uid: user.uid,
        displayName: user.displayName || "",
        employeeId: user.employeeId || "",
        email: user.email || "",
        department: user.department || "",
        role: user.role || "",
        removedBy: userProfile?.displayName || userProfile?.email || "Admin",
        removedAt: new Date().toISOString(),
        previousTeamLead: user.teamLeadName || "None",
        status: "archived",
        originalUserData: { ...user, isActive: false },
      };

      await setDoc(doc(db, "archived_users", user.uid), archiveData);
      await deleteDoc(doc(db, "users", user.uid));
      setCreateOk(`✅ ${user.displayName} Deactivated successfully.`);
      setTimeout(() => setCreateOk(""), 3000);
    } catch (err) {
      setCreateErr(err.message || "Failed to deactivate employee.");
      setTimeout(() => setCreateErr(""), 3000);
    }
  }

  async function restoreEmployee(archivedUser) {
    const confirmRestore = window.confirm(
      `Are you sure you want to restore ${archivedUser.displayName}'s account?`
    );
    if (!confirmRestore) return;

    try {
      const original = archivedUser.originalUserData || {
        uid: archivedUser.uid,
        displayName: archivedUser.displayName,
        employeeId: archivedUser.employeeId,
        email: archivedUser.email,
        department: archivedUser.department,
        role: archivedUser.role,
        isActive: true,
      };

      original.isActive = true;
      original.updatedAt = serverTimestamp();

      await setDoc(doc(db, "users", archivedUser.uid), original);
      await deleteDoc(doc(db, "archived_users", archivedUser.uid));
      setCreateOk(`✅ ${archivedUser.displayName} restored successfully.`);
      setTimeout(() => setCreateOk(""), 3000);
    } catch (err) {
      setCreateErr(err.message || "Failed to restore employee.");
      setTimeout(() => setCreateErr(""), 3000);
    }
  }

  async function deletePermanently(archivedUser) {
    const confirmDelete = window.confirm(
      `Are you sure you want to PERMANENTLY delete ${archivedUser.displayName}? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "archived_users", archivedUser.uid));
      setCreateOk(`✅ ${archivedUser.displayName} permanently deleted.`);
      setTimeout(() => setCreateOk(""), 3000);
    } catch (err) {
      setCreateErr(err.message || "Failed to delete employee permanently.");
      setTimeout(() => setCreateErr(""), 3000);
    }
  }

  function openEdit(user) {
    setEditingUser(user);
    setEditData({
      displayName: user.displayName || "",
      email: user.email || "",
      role: user.role || "employee",
      department: user.department || "Engineering",
      employeeId: user.employeeId || "",
      teamId: user.teamId || "",
      shiftId: user.shiftId || "",
      employmentType: user.employmentType === "intern" ? "paid-intern" : (user.employmentType || "full-time"),
      joiningDate: user.joiningDate || "",
      internshipDuration: user.internshipDuration ? String(user.internshipDuration) : "3",
      isActive: user.isActive !== undefined ? user.isActive : true,

      firstName: user.firstName || "",
      middleName: user.middleName || "",
      lastName: user.lastName || "",
      gender: user.gender || "Male",
      dateOfBirth: user.dateOfBirth || "",
      maritalStatus: user.maritalStatus || "Single",
      bloodGroup: user.bloodGroup || "O+",
      nationality: user.nationality || "Indian",
      personalEmail: user.personalEmail || "",
      phone: user.phone || "",
      alternateMobile: user.alternateMobile || "",
      emergencyContact: user.emergencyContact || "",

      confirmationDate: user.confirmationDate || "",
      probationPeriod: user.probationPeriod ? String(user.probationPeriod) : "6",
      subDepartmentId: user.subDepartmentId || "",
      hrManagerId: user.hrManagerId || "",

      // Personal & address defaults — filled in fully once editPersonal loads,
      // but must never be undefined in the meantime (Firestore rejects `undefined`).
      fatherName: "",
      motherName: "",
      spouseName: "",
      aadhaar: "",
      pan: "",
      passport: "",
      drivingLicense: "",
      voterId: "",
      passportExpiry: "",
      visa: "",
      permLine1: "",
      permLine2: "",
      permCity: "",
      permState: "",
      permCountry: "India",
      permPincode: "",
      currLine1: "",
      currLine2: "",
      currCity: "",
      currState: "",
      currCountry: "India",
      currPincode: "",
      sameAsPermanent: false,

      // Bank/payroll defaults — filled in fully once editBank loads.
      bankName: "",
      bankBranch: "",
      accountNumber: "",
      ifscCode: "",
      accountHolder: "",
      upiId: "",
      isSalaryAccount: true,
      ctc: "0",
      basicSalary: "0",
      hra: "0",
      taxRegime: "New Regime",
      pfApplicable: true,
      pfNumber: "",
      esiApplicable: false,
      esiNumber: "",
      professionalTax: true,
      lwf: true,
      tdsCategoryId: "",
    });
    setEditTab("employment");
    setShowEdit(true);
    setShowForm(false);
    setCreateErr("");
    setCreateOk("");
  }

  async function handleApproveRemoval(requestId, employeeUid) {
    if (!window.confirm(`Approve this removal request? The employee will be removed from their team.`)) return;
    setApprovingRemoval((p) => ({ ...p, [requestId]: true }));
    setRemovalMsg("");
    try {
      await approveRemovalRequest(requestId, userProfile.uid);
      setRemovalMsg(`✅ Employee removed from team successfully.`);
      setTimeout(() => setRemovalMsg(""), 3000);
    } catch (err) {
      console.error("[AdminEmployees] approve removal error:", err);
      setRemovalMsg(`❌ Failed to approve removal: ${err.message}`);
    }
    setApprovingRemoval((p) => ({ ...p, [requestId]: false }));
  }

  async function handleRejectRemoval(requestId) {
    const reason = prompt("Why are you rejecting this removal request?");
    if (reason === null) return;
    setRejectingRemoval((p) => ({ ...p, [requestId]: true }));
    setRemovalMsg("");
    try {
      await rejectRemovalRequest(requestId, userProfile.uid, reason || "Rejected by Admin");
      setRemovalMsg(`✅ Removal request rejected.`);
      setTimeout(() => setRemovalMsg(""), 3000);
    } catch (err) {
      console.error("[AdminEmployees] reject removal error:", err);
      setRemovalMsg(`❌ Failed to reject removal: ${err.message}`);
    }
    setRejectingRemoval((p) => ({ ...p, [requestId]: false }));
  }



  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      {/* Wizard Embedded Styles */}
      <style>{`
        .wizard-steps-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding: 1rem 0;
          border-bottom: 1px solid var(--border);
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .step-indicator {
          flex: 1;
          min-width: 140px;
          text-align: center;
          padding: 0.75rem 0.5rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text-muted);
          transition: all 0.3s ease;
        }
        .step-indicator--active {
          background: var(--brand-primary, #1b4b31);
          color: white;
          border-color: var(--brand-primary, #1b4b31);
          box-shadow: 0 0 10px rgba(27, 75, 49, 0.2);
        }
        .wizard-footer-btns {
          display: flex;
          justify-content: space-between;
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border);
        }
        .form-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1.25rem;
          border-left: 3px solid var(--brand-primary, #1b4b31);
          padding-left: 0.5rem;
        }
        .confirm-review-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
        }
        .confirm-review-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 1rem;
        }
        .confirm-review-card h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
          color: var(--brand-primary, #1b4b31);
          font-size: 0.85rem;
          text-transform: uppercase;
        }
        .confirm-review-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.8rem;
          margin-bottom: 0.4rem;
        }
        .confirm-review-label {
          color: var(--text-muted);
        }
        .confirm-review-val {
          font-weight: 500;
          text-align: right;
          max-width: 60%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .edit-tab-header {
          display: flex;
          gap: 0.5rem;
          border-bottom: 1px solid var(--border);
          margin-bottom: 1.5rem;
          overflow-x: auto;
          padding-bottom: 0.25rem;
        }
        .edit-tab-btn {
          padding: 0.6rem 1rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.85rem;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          white-space: nowrap;
        }
        .edit-tab-btn--active {
          color: var(--brand-primary, #1b4b31);
          border-bottom-color: var(--brand-primary, #1b4b31);
        }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">
            {users.length} total users in the system
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setShowForm(!showForm);
            setShowEdit(false);
            setCreateErr("");
            setCreateOk("");
            setCurrentStep(1);
          }}
        >
          {showForm ? "✕ Cancel" : "+ Add Employee"}
        </button>
      </div>

      {createOk && <div className="form-success">{createOk}</div>}
      {createErr && <div className="form-error">{createErr}</div>}
      {removalMsg && (
        <div
          className={removalMsg.startsWith("✅") ? "form-success" : "form-error"}
          style={{ marginBottom: "1rem" }}
        >
          {removalMsg}
        </div>
      )}

      {/* Pending Removal Requests */}
      {pendingRemovalRequests.length > 0 && (
        <div className="form-card" style={{ marginBottom: "1.5rem" }}>
          <h3 className="form-card-title">
            Pending Removal Requests ({pendingRemovalRequests.length})
          </h3>
          <p className="text-muted mt-1" style={{ marginBottom: "1rem" }}>
            Team Leads have requested removal of these employees. Review and approve or reject each request.
          </p>
          {removalReqsLoading ? (
            <div style={{ padding: "1rem" }}>Loading requests…</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {pendingRemovalRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    padding: "0.875rem",
                    border: "1px solid var(--border)",
                    borderRadius: "6px",
                    backgroundColor: "var(--bg-secondary)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "500", marginBottom: "0.25rem" }}>
                      {req.employeeName}
                      {req.employeeId && (
                        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginLeft: "0.5rem" }}>
                          ({req.employeeId})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>
                      Requested by {req.teamLeadName}
                    </div>
                    {req.reason && (
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                        Reason: {req.reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      className="btn-ghost-sm"
                      style={{ color: "var(--green)" }}
                      onClick={() => handleApproveRemoval(req.id, req.employeeUid)}
                      disabled={approvingRemoval[req.id]}
                    >
                      {approvingRemoval[req.id] ? "Approving…" : "✓ Approve"}
                    </button>
                    <button
                      className="btn-ghost-sm"
                      style={{ color: "var(--red)" }}
                      onClick={() => handleRejectRemoval(req.id)}
                      disabled={rejectingRemoval[req.id]}
                    >
                      {rejectingRemoval[req.id] ? "Rejecting…" : "✕ Reject"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Multi-Step Onboarding Wizard */}
      {showForm && (
        <div className="form-card" style={{ marginBottom: "1.5rem" }}>
          <h3 className="form-card-title">Employee Onboarding Wizard</h3>
          <p className="text-muted mt-1" style={{ marginBottom: "1rem" }}>
            Follow the steps to construct the employee's master registry record.
          </p>

          <div className="wizard-steps-header">
            <div className={`step-indicator ${currentStep === 1 ? "step-indicator--active" : ""} ${currentStep > 1 ? "step-indicator--completed" : ""}`}>
              1. Basic Identity
            </div>
            <div className={`step-indicator ${currentStep === 2 ? "step-indicator--active" : ""} ${currentStep > 2 ? "step-indicator--completed" : ""}`}>
              2. Employment & Org
            </div>
            <div className={`step-indicator ${currentStep === 3 ? "step-indicator--active" : ""} ${currentStep > 3 ? "step-indicator--completed" : ""}`}>
              3. Personal Details
            </div>
            <div className={`step-indicator ${currentStep === 4 ? "step-indicator--active" : ""} ${currentStep > 4 ? "step-indicator--completed" : ""}`}>
              4. Bank & Payroll
            </div>
            <div className={`step-indicator ${currentStep === 5 ? "step-indicator--active" : ""}`}>
              5. Final Review
            </div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="form-grid">
            
            {/* STEP 1: Basic Identity */}
            {currentStep === 1 && (
              <>
                <div className="form-group form-group--full">
                  <div className="form-section-title">Step 1: Basic Identity Information</div>
                </div>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    name="displayName"
                    value={formData.displayName}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                      setFormData((p) => ({ ...p, displayName: value }));
                    }}
                    required
                    placeholder="Ananya Sharma"
                    className={fieldErrors.displayName ? "input-error" : ""}
                  />
                  {fieldErrors.displayName && <span className="form-error">{fieldErrors.displayName}</span>}
                </div>
                <div className="form-group">
                  <label>Official Email * (Unique System Login)</label>
                  <input
                    name="email"
                    type="text"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="ananya.sharma@company.com"
                    className={fieldErrors.email ? "input-error" : ""}
                  />
                  {fieldErrors.email && <span className="form-error">{fieldErrors.email}</span>}
                </div>
                <div className="form-group">
                  <label>Personal Email</label>
                  <input
                    name="personalEmail"
                    type="email"
                    value={formData.personalEmail}
                    onChange={handleChange}
                    placeholder="ananya.sharma@gmail.com"
                  />
                </div>
                <div className="form-group">
                  <label>Login Password * (Min 6 chars)</label>
                  <div className="input-with-icon">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="New user's login password"
                      className={fieldErrors.password ? "input-error" : ""}
                    />
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  {fieldErrors.password && <span className="form-error">{fieldErrors.password}</span>}
                </div>

                <div className="form-group">
                  <label>Gender *</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className={fieldErrors.dateOfBirth ? "input-error" : ""}
                  />
                  {fieldErrors.dateOfBirth && <span className="form-error">{fieldErrors.dateOfBirth}</span>}
                </div>
                <div className="form-group">
                  <label>Marital Status</label>
                  <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Blood Group</label>
                  <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91-9876543210"
                    className={fieldErrors.phone ? "input-error" : ""}
                  />
                  {fieldErrors.phone && <span className="form-error">{fieldErrors.phone}</span>}
                </div>
                <div className="form-group">
                  <label>Alternate Mobile</label>
                  <input
                    name="alternateMobile"
                    value={formData.alternateMobile}
                    onChange={handleChange}
                    placeholder="+91-9123456780"
                    className={fieldErrors.alternateMobile ? "input-error" : ""}
                  />
                  {fieldErrors.alternateMobile && <span className="form-error">{fieldErrors.alternateMobile}</span>}
                </div>
                <div className="form-group">
                  <label>Nationality</label>
                  <input
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            {/* STEP 2: Employment Details */}
            {currentStep === 2 && (
              <>
                <div className="form-group form-group--full">
                  <div className="form-section-title">Step 2: Employment Details & Org Mapping</div>
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input
                      type="checkbox"
                      checked={autoGenerateId}
                      onChange={(e) => setAutoGenerateId(e.target.checked)}
                      style={{ width: "auto" }}
                    />
                    Auto-generate Employee ID
                  </label>
                  {!autoGenerateId && (
                    <input
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleChange}
                      placeholder="e.g. EMP-231"
                      style={{ marginTop: "0.5rem" }}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select name="role" value={formData.role} onChange={handleChange}>
                    <option value="employee">Employee</option>
                    <option value="lead">Team Lead</option>
                    <option value="hr">HR</option>
                      <option value="finance">Finance</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Employment Type *</label>
                  <select
                    name="employmentType"
                    value={formData.employmentType}
                    onChange={handleChange}
                    disabled={formData.role === "admin" || formData.role === "lead" || formData.role === "hr"}
                  >
                    <option value="full-time">Full Time</option>
                    <option value="paid-intern">Paid Intern</option>
                    <option value="unpaid-intern">Unpaid Intern</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Joining *</label>
                  <input
                    name="joiningDate"
                    type="date"
                    value={formData.joiningDate}
                    onChange={handleChange}
                    className={fieldErrors.joiningDate ? "input-error" : ""}
                  />
                  {fieldErrors.joiningDate && <span className="form-error">{fieldErrors.joiningDate}</span>}
                </div>
                <div className="form-group">
                  <label>Probation Period (Months)</label>
                  <input
                    name="probationPeriod"
                    type="number"
                    value={formData.probationPeriod}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Confirmation Date</label>
                  <input
                    name="confirmationDate"
                    type="date"
                    value={formData.confirmationDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
  <label>Department *</label>
  <select
    name="department"
    value={formData.department}
    onChange={handleChange}
    disabled={formData.role === "admin" || formData.role === "finance"}
  >
    {formData.role === "admin" ? (
      <option value="Administration">Administration</option>
    ) : formData.role === "finance" ? (
      <option value="Finance">Finance</option>
    ) : formData.role === "lead" ? (
      activeDepts.filter(d => !["Administration", "HR", "Finance"].includes(d)).map((d) => (
        <option key={d} value={d}>{d}</option>
      ))
    ) : (
      activeDepts.filter(d => !["Administration", "Finance"].includes(d)).map((d) => (
        <option key={d} value={d}>{d}</option>
      ))
    )}
  </select>
</div>

                <div className="form-group">
                  <label>Assign Shift *</label>
                  <select name="shiftId" value={formData.shiftId} onChange={handleChange}>
                    <option value="">— Select Shift —</option>
                    {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.startTime}-{s.endTime})</option>)}
                  </select>
                </div>
                {formData.role === "employee" && (
                  <div className="form-group">
                    <label>Reporting Manager</label>
                    <select name="teamId" value={formData.teamId} onChange={handleChange}>
                      <option value="">— No team lead assigned —</option>
                      {leads.map((l) => (
                        <option key={l.uid} value={l.uid}>{l.displayName} ({l.department})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>HR Point of Contact</label>
                  <select name="hrManagerId" value={formData.hrManagerId} onChange={handleChange}>
                    <option value="">— No HR Lead assigned —</option>
                    {users.filter(u => u.role === "admin" || u.department === "HR").map(u => (
                      <option key={u.uid} value={u.displayName}>{u.displayName}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* STEP 3: Personal & Address Details */}
            {currentStep === 3 && (
              <>
                <div className="form-group form-group--full">
                  <div className="form-section-title">Step 3: Personal & Address Information</div>
                </div>
                <div className="form-group">
                  <label>Father's Name *</label>
                  <input name="fatherName" value={formData.fatherName} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Mother's Name</label>
                  <input name="motherName" value={formData.motherName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Spouse Name</label>
                  <input name="spouseName" value={formData.spouseName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Aadhaar Card Number (12 Digits) *</label>
                  <input
                    name="aadhaar"
                    value={formData.aadhaar}
                    onChange={handleChange}
                    placeholder="XXXX-XXXX-XXXX"
                    className={fieldErrors.aadhaar ? "input-error" : ""}
                  />
                  {fieldErrors.aadhaar && <span className="form-error">{fieldErrors.aadhaar}</span>}
                </div>
                <div className="form-group">
                  <label>PAN Card Number (AAAAA9999A) *</label>
                  <input
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    placeholder="ABCDE1234F"
                    className={fieldErrors.pan ? "input-error" : ""}
                  />
                  {fieldErrors.pan && <span className="form-error">{fieldErrors.pan}</span>}
                </div>
                <div className="form-group">
                  <label>Passport Number</label>
                  <input name="passport" value={formData.passport} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Passport Expiry</label>
                  <input name="passportExpiry" type="date" value={formData.passportExpiry} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Emergency Contact Mobile * (Different from own)</label>
                  <input
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleChange}
                    placeholder="+91-9988776655"
                    className={fieldErrors.emergencyContact ? "input-error" : ""}
                  />
                  {fieldErrors.emergencyContact && <span className="form-error">{fieldErrors.emergencyContact}</span>}
                </div>
                <div className="form-group form-group--full">
                  <h4 style={{ margin: "1rem 0 0.5rem 0", fontSize: "0.9rem" }}>Permanent Address</h4>
                </div>
                <div className="form-group">
                  <label>Address Line 1 *</label>
                  <input name="permLine1" value={formData.permLine1} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Address Line 2</label>
                  <input name="permLine2" value={formData.permLine2} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input name="permCity" value={formData.permCity} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input name="permState" value={formData.permState} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Country *</label>
                  <input name="permCountry" value={formData.permCountry} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Pincode *</label>
                  <input name="permPincode" value={formData.permPincode} onChange={handleChange} required />
                </div>
                <div className="form-group form-group--full">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "1rem 0" }}>
                    <input
                      type="checkbox"
                      name="sameAsPermanent"
                      checked={formData.sameAsPermanent}
                      onChange={(e) => setFormData((p) => ({ ...p, sameAsPermanent: e.target.checked }))}
                      style={{ width: "auto" }}
                    />
                    Current Address same as Permanent Address
                  </label>
                </div>
                {!formData.sameAsPermanent && (
                  <>
                    <div className="form-group form-group--full">
                      <h4 style={{ margin: "0.5rem 0", fontSize: "0.9rem" }}>Current Address</h4>
                    </div>
                    <div className="form-group">
                      <label>Address Line 1 *</label>
                      <input name="currLine1" value={formData.currLine1} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Address Line 2</label>
                      <input name="currLine2" value={formData.currLine2} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label>City *</label>
                      <input name="currCity" value={formData.currCity} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <input name="currState" value={formData.currState} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Country *</label>
                      <input name="currCountry" value={formData.currCountry} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Pincode *</label>
                      <input name="currPincode" value={formData.currPincode} onChange={handleChange} required />
                    </div>
                  </>
                )}
              </>
            )}

            {/* STEP 4: Bank & Payroll Configuration */}
            {currentStep === 4 && (
              <>
                <div className="form-group form-group--full">
                  <div className="form-section-title">Step 4: Bank Details & Payroll Settings</div>
                </div>
                <div className="form-group">
                  <label>Bank Name *</label>
                  <input name="bankName" value={formData.bankName} onChange={handleChange} placeholder="e.g. HDFC Bank" required />
                </div>
                <div className="form-group">
                  <label>Bank Branch *</label>
                  <input name="bankBranch" value={formData.bankBranch} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Account Number *</label>
                  <input name="accountNumber" value={formData.accountNumber} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>IFSC Code *</label>
                  <input
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleChange}
                    placeholder="HDFC0001234"
                    className={fieldErrors.ifscCode ? "input-error" : ""}
                  />
                  {fieldErrors.ifscCode && <span className="form-error">{fieldErrors.ifscCode}</span>}
                </div>
                <div className="form-group">
                  <label>Account Holder *</label>
                  <input name="accountHolder" value={formData.accountHolder} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>UPI ID</label>
                  <input name="upiId" value={formData.upiId} onChange={handleChange} placeholder="username@upi" />
                </div>
                <div className="form-group">
                  <label>Annual CTC (INR) *</label>
                  <input
                    name="ctc"
                    type="number"
                    value={formData.ctc}
                    onChange={handleChange}
                    className={fieldErrors.ctc ? "input-error" : ""}
                  />
                  {fieldErrors.ctc && <span className="form-error">{fieldErrors.ctc}</span>}
                </div>
                <div className="form-group">
                  <label>Basic Salary (INR) *</label>
                  <input name="basicSalary" type="number" value={formData.basicSalary} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>HRA (INR) *</label>
                  <input name="hra" type="number" value={formData.hra} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Tax Regime *</label>
                  <select name="taxRegime" value={formData.taxRegime} onChange={handleChange}>
                    <option value="New Regime">New Regime</option>
                    <option value="Old Regime">Old Regime</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1.5rem" }}>
                    <input
                      type="checkbox"
                      name="pfApplicable"
                      checked={formData.pfApplicable}
                      onChange={(e) => setFormData((p) => ({ ...p, pfApplicable: e.target.checked }))}
                      style={{ width: "auto" }}
                    />
                    PF Applicable
                  </label>
                </div>
                {formData.pfApplicable && (
                  <div className="form-group">
                    <label>PF Number / UAN</label>
                    <input name="pfNumber" value={formData.pfNumber} onChange={handleChange} />
                  </div>
                )}
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1.5rem" }}>
                    <input
                      type="checkbox"
                      name="esiApplicable"
                      checked={formData.esiApplicable}
                      onChange={(e) => setFormData((p) => ({ ...p, esiApplicable: e.target.checked }))}
                      style={{ width: "auto" }}
                    />
                    ESI Applicable
                  </label>
                </div>
                {formData.esiApplicable && (
                  <div className="form-group">
                    <label>ESI Number</label>
                    <input name="esiNumber" value={formData.esiNumber} onChange={handleChange} />
                  </div>
                )}
              </>
            )}

            {/* STEP 5: Final Review & Confirmation */}
            {currentStep === 5 && (
              <>
                <div className="form-group form-group--full">
                  <div className="form-section-title">Step 5: Review Employee Master Record</div>
                  <p className="text-muted" style={{ marginBottom: "1rem" }}>
                    Please review all parameters before creating the employee account.
                  </p>
                </div>

                <div className="form-group form-group--full confirm-review-grid">
                  <div className="confirm-review-card">
                    <h4>Basic Info</h4>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Name:</span>
                      <span className="confirm-review-val">{formData.displayName}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Email:</span>
                      <span className="confirm-review-val">{formData.email}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Phone:</span>
                      <span className="confirm-review-val">{formData.phone || "—"}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">DOB:</span>
                      <span className="confirm-review-val">{formData.dateOfBirth || "—"}</span>
                    </div>
                  </div>

                  <div className="confirm-review-card">
                    <h4>Employment</h4>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Role:</span>
                      <span className="confirm-review-val" style={{ textTransform: "capitalize" }}>{formData.role}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Type:</span>
                      <span className="confirm-review-val" style={{ textTransform: "capitalize" }}>{formData.employmentType}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Department:</span>
                      <span className="confirm-review-val">{formData.department}</span>
                    </div>
                  </div>

                  <div className="confirm-review-card">
                    <h4>Identities & Address</h4>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Aadhaar:</span>
                      <span className="confirm-review-val">{formData.aadhaar ? `XXXX-XXXX-${formData.aadhaar.slice(-4)}` : "—"}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">PAN:</span>
                      <span className="confirm-review-val">{formData.pan || "—"}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">City:</span>
                      <span className="confirm-review-val">{formData.permCity || "—"}</span>
                    </div>
                  </div>

                  <div className="confirm-review-card">
                    <h4>Bank & CTC</h4>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Bank:</span>
                      <span className="confirm-review-val">{formData.bankName}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">Account:</span>
                      <span className="confirm-review-val">{formData.accountNumber ? `XXXXXX${formData.accountNumber.slice(-4)}` : "—"}</span>
                    </div>
                    <div className="confirm-review-item">
                      <span className="confirm-review-label">CTC:</span>
                      <span className="confirm-review-val">{Number(formData.ctc).toLocaleString("en-IN")} INR</span>
                    </div>
                  </div>
                </div>

                <div
                  className="form-group form-group--full"
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: "1.5rem",
                    marginTop: "1.5rem",
                  }}
                >
                  <label><strong>Your Admin Password *</strong> — required to confirm account creation</label>
                  <div className="input-with-icon" style={{ maxWidth: "400px", marginTop: "0.5rem" }}>
                    <input
                      type={showAdminPassword ? "text" : "password"}
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      required
                      placeholder="Your login password"
                    />
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => setShowAdminPassword((v) => !v)}
                    >
                      {showAdminPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step Controls */}
            <div className="form-group form-group--full wizard-footer-btns">
              {currentStep > 1 ? (
                <button type="button" className="btn-secondary" onClick={handlePrev} disabled={creating}>
                  ← Previous
                </button>
              ) : (
                <div />
              )}
              {currentStep < 5 ? (
                <button type="button" className="btn-primary" onClick={handleNext}>
                  Next →
                </button>
              ) : (
                <button type="button" className="btn-success" onClick={handleCreate} disabled={creating}>
                  {creating ? "Creating Account…" : "🚀 Confirm & Onboard"}
                </button>
              )}
            </div>

          </form>
        </div>
      )}

      {/* Edit Form with tabbed layout */}
      {showEdit && (
        <div className="form-card" style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 className="form-card-title">Edit Employee Registry</h3>
            <button className="btn-danger-sm" onClick={() => setShowEdit(false)}>✕ Close</button>
          </div>

          <div className="edit-tab-header">
            <button type="button" className={`edit-tab-btn ${editTab === "employment" ? "edit-tab-btn--active" : ""}`} onClick={() => setEditTab("employment")}>
              1. Employment & Shift
            </button>
            <button type="button" className={`edit-tab-btn ${editTab === "personal" ? "edit-tab-btn--active" : ""}`} onClick={() => setEditTab("personal")}>
              2. Personal Details
            </button>
            <button type="button" className={`edit-tab-btn ${editTab === "bank" ? "edit-tab-btn--active" : ""}`} onClick={() => setEditTab("bank")}>
              3. Bank & Salary
            </button>
          </div>

          <form onSubmit={handleUpdate} className="form-grid">
            {editTab === "employment" && (
              <>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input name="displayName" value={editData.displayName} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input name="email" type="email" value={editData.email} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Employee ID *</label>
                  <input name="employeeId" value={editData.employeeId} onChange={handleEditChange} required disabled />
                </div>

                <div className="form-group">
                  <label>Role *</label>
                  <select name="role" value={editData.role} onChange={handleEditChange}>
  <option value="employee">Employee</option>
  <option value="lead">Team Lead</option>
  <option value="hr">HR</option>
  <option value="finance">Finance</option>
  <option value="admin">Admin</option>
</select>
                </div>
                <div className="form-group">
                  <label>Department *</label>
                  <select name="department" value={editData.department} onChange={handleEditChange} disabled={editData.role === "admin" || editData.role === "finance"}>
  {editData.role === "admin" ? (
    <option value="Administration">Administration</option>
  ) : editData.role === "finance" ? (
    <option value="Finance">Finance</option>
  ) : editData.role === "lead" ? (
    activeDepts.filter(d => !["Administration", "HR", "Finance"].includes(d)).map((d) => (
      <option key={d} value={d}>{d}</option>
    ))
  ) : (
    activeDepts.filter(d => !["Administration", "Finance"].includes(d)).map((d) => (
      <option key={d} value={d}>{d}</option>
    ))
  )}
</select>
                </div>

                <div className="form-group">
                  <label>Shift Assignment</label>
                  <select name="shiftId" value={editData.shiftId} onChange={handleEditChange}>
                    <option value="">— No shift assigned —</option>
                    {shifts.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.startTime} - {s.endTime})</option>
                    ))}
                  </select>
                </div>
                {editData.role === "employee" && (
                  <div className="form-group">
                    <label>Team Lead</label>
                    <select name="teamId" value={editData.teamId} onChange={handleEditChange}>
                      <option value="">— No team lead assigned —</option>
                      {leads.map((l) => (
                        <option key={l.uid} value={l.uid}>{l.displayName} ({l.department})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Employment Type</label>
                  <select name="employmentType" value={editData.employmentType} onChange={handleEditChange} disabled={editData.role === "admin" || editData.role === "lead"}>
                    <option value="full-time">Full Time</option>
                    <option value="paid-intern">Paid Intern</option>
                    <option value="unpaid-intern">Unpaid Intern</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Joining</label>
                  <input name="joiningDate" type="date" value={editData.joiningDate} onChange={handleEditChange} max={new Date().toISOString().split("T")[0]} />
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1.5rem" }}>
                    <input type="checkbox" name="isActive" checked={editData.isActive} onChange={handleEditChange} style={{ width: "auto" }} />
                    Account Active
                  </label>
                </div>
              </>
            )}

            {editTab === "personal" && (
              <>
                <div className="form-group">
                  <label>Father's Name</label>
                  <input name="fatherName" value={editData.fatherName} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Mother's Name</label>
                  <input name="motherName" value={editData.motherName} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Spouse Name</label>
                  <input name="spouseName" value={editData.spouseName} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Aadhaar Card</label>
                  <input name="aadhaar" value={editData.aadhaar} onChange={handleEditChange} placeholder="XXXX-XXXX-XXXX" />
                </div>
                <div className="form-group">
                  <label>PAN Card</label>
                  <input name="pan" value={editData.pan} onChange={handleEditChange} placeholder="ABCDE1234F" />
                </div>
                <div className="form-group">
                  <label>Passport</label>
                  <input name="passport" value={editData.passport} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Mobile *</label>
                  <input name="phone" value={editData.phone} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Emergency Contact *</label>
                  <input name="emergencyContact" value={editData.emergencyContact} onChange={handleEditChange} />
                </div>
                <div className="form-group form-group--full">
                  <h4 style={{ margin: "1rem 0 0.5rem 0", fontSize: "0.9rem" }}>Permanent Address</h4>
                </div>
                <div className="form-group">
                  <label>Address Line 1</label>
                  <input name="permLine1" value={editData.permLine1} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input name="permCity" value={editData.permCity} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input name="permState" value={editData.permState} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Pincode</label>
                  <input name="permPincode" value={editData.permPincode} onChange={handleEditChange} />
                </div>
                <div className="form-group form-group--full">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input type="checkbox" name="sameAsPermanent" checked={editData.sameAsPermanent} onChange={handleEditChange} style={{ width: "auto" }} />
                    Current same as Permanent Address
                  </label>
                </div>
                {!editData.sameAsPermanent && (
                  <>
                    <div className="form-group form-group--full">
                      <h4 style={{ margin: "0.5rem 0", fontSize: "0.9rem" }}>Current Address</h4>
                    </div>
                    <div className="form-group">
                      <label>Address Line 1</label>
                      <input name="currLine1" value={editData.currLine1} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input name="currCity" value={editData.currCity} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input name="currState" value={editData.currState} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                      <label>Pincode</label>
                      <input name="currPincode" value={editData.currPincode} onChange={handleEditChange} />
                    </div>
                  </>
                )}
              </>
            )}

            {editTab === "bank" && (
              <>
                <div className="form-group">
                  <label>Bank Name</label>
                  <input name="bankName" value={editData.bankName} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Branch</label>
                  <input name="bankBranch" value={editData.bankBranch} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input name="accountNumber" value={editData.accountNumber} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input name="ifscCode" value={editData.ifscCode} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Account Holder</label>
                  <input name="accountHolder" value={editData.accountHolder} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>UPI ID</label>
                  <input name="upiId" value={editData.upiId} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Annual CTC (INR)</label>
                  <input name="ctc" type="number" value={editData.ctc} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Basic Salary (INR)</label>
                  <input name="basicSalary" type="number" value={editData.basicSalary} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>HRA (INR)</label>
                  <input name="hra" type="number" value={editData.hra} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Tax Regime</label>
                  <select name="taxRegime" value={editData.taxRegime} onChange={handleEditChange}>
                    <option value="New Regime">New Regime</option>
                    <option value="Old Regime">Old Regime</option>
                  </select>
                </div>
              </>
            )}

            <div className="form-group form-group--full" style={{ marginTop: "1rem", borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
              <button type="submit" className="btn-primary" disabled={updating}>
                {updating ? "Saving changes…" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="table-filters">
        <input
          className="filter-search"
          placeholder="Search by name, email, or ID…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
        />
        <select
          className="filter-select"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin ({users.filter(u => u.role === "admin").length})</option>
          <option value="hr">HR ({users.filter(u => u.role === "hr").length})</option>
          <option value="finance">Finance ({users.filter(u => u.role === "finance").length})</option>
          <option value="lead">Team Lead ({users.filter(u => u.role === "lead").length})</option>
          <option value="employee">Employee ({users.filter(u => u.role === "employee").length})</option>
        </select>
        <select
          className="filter-select"
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
        >
          <option value="all">All Departments</option>
          {activeDepts.map((d) => (
            <option key={d} value={d}>
              {d} ({users.filter((u) => u.department === d).length})
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterIntern}
          onChange={(e) => setFilterIntern(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="full-time">Full Time Employee</option>
          <option value="paid-intern">Paid Intern</option>
          <option value="unpaid-intern">Unpaid Intern</option>
          <option value="completing">Completing Soon</option>
          <option value="completed">Completed</option>
        </select>
        <select
          className="filter-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="newest">Newly Added First</option>
          <option value="oldest">Oldest First</option>
          <option value="a-z">A-Z</option>
          <option value="z-a">Z-A</option>
        </select>
        <button
          type="button"
          className="btn-secondary"
          onClick={handleExportExcel}
          disabled={sortedAndFiltered.length === 0}
          title="Download the currently filtered employees as an Excel file"
        >
          ⬇ Download Excel ({sortedAndFiltered.length})
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="card-loading">Loading employees…</div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Email</th>
                <th>Department</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFiltered.map((u) => (
                <tr key={u.uid}>
                  <td>
                    <div className="table-user-cell">
                      <div className="table-avatar">
                        {u.displayName?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                          {u.displayName}
                          {(u.employmentType === "paid-intern" || u.employmentType === "unpaid-intern" || u.employmentType === "intern") && (
                            <span className="intern-badge">
                              {(() => {
                                const end = u.internshipEndDate ? new Date(u.internshipEndDate) : null;
                                const days = end ? Math.ceil((end - new Date()) / 86400000) : 0;
                                if (days <= 0) return "Completed";
                                return `${days} Days Left`;
                              })()}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                          {u.designation || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>
                    {u.employeeId}
                  </td>
                  <td style={{ fontSize: "0.8rem" }}>{u.email}</td>
                  <td>{u.department}</td>
                  <td>
                    <RolePill role={u.role} />
                  </td>
                  <td>
                    <span className={`status-pill status-pill--${u.isActive ? "green" : "red"}`}>
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="action-btns" style={{ display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "flex-start", flexWrap: "nowrap" }}>
                      <button className="btn-ghost-sm" onClick={() => setViewUser(u)}>
                        View
                      </button>
                      <button className="btn-ghost-sm" onClick={() => openEdit(u)}>
                        Edit
                      </button>
                      <button
                        className="btn-danger-sm"
                        onClick={() => removeEmployee(u)}
                        disabled={u.uid === userProfile?.uid}
                        title={u.uid === userProfile?.uid ? "Cannot remove yourself" : ""}
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedAndFiltered.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-empty">
                    No employees match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Past Members Section */}
      {archivedUsers && archivedUsers.length > 0 && (
        <div className="form-card" style={{ marginTop: "2rem" }}>
          <h3 className="form-card-title">Past Members / Deleted Accounts</h3>
          <p className="text-muted mt-1" style={{ marginBottom: "1.5rem" }}>
            History of Deactivated employees. You can restore their accounts or permanently delete them.
          </p>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Deactivated By</th>
                  <th>Deactivated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {archivedUsers.map((au) => (
                  <tr key={au.uid || au.id}>
                    <td>{au.displayName}</td>
                    <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>
                      {au.employeeId}
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>{au.email}</td>
                    <td>{au.department}</td>
                    <td>
                      <RolePill role={au.role} />
                    </td>
                    <td>{au.removedBy}</td>
                    <td style={{ fontSize: "0.8rem" }}>
                      {au.removedAt?.toDate
                        ? au.removedAt.toDate().toLocaleDateString()
                        : au.removedAt
                        ? new Date(au.removedAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="btn-ghost-sm" style={{ color: "var(--green)" }} onClick={() => restoreEmployee(au)}>
                          Restore
                        </button>
                        <button className="btn-danger-sm" onClick={() => deletePermanently(au)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Employee Overview Drawer */}
      {viewUser && (
        <EmployeeOverviewDrawer
          user={viewUser}
          shifts={shifts}
          allUsers={users}
          onClose={() => setViewUser(null)}
        />
      )}
    </Layout>
  );
}

function RolePill({ role }) {
  const map = { admin: "blue", hr: "green", lead: "purple", employee: "gray", finance: "orange" };
  const labels = ROLE_LABELS;
  return (
    <span className={`status-pill status-pill--${map[role] || "gray"}`}>
      {labels[role] || role}
    </span>
  );
}